// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatTime } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Loader2, 
  Trophy, 
  Users, 
  Clock,
  MapPin,
  Play,
  Square,
  CheckCircle,
  AlertCircle,
  Target
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  status: string;
}

interface Match {
  id: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  team1_id?: string;
  team2_id?: string;
  team1_name?: string;
  team2_name?: string;
  team1_score?: number;
  team2_score?: number;
  winner_id?: string;
  status: 'scheduled' | 'in_progress' | 'completed';
  scheduled_time?: string;
  venue_id?: string;
  court_field?: string;
  venues?: {
    name: string;
  };
}

interface MatchScoringDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
}

export const MatchScoringDialog: React.FC<MatchScoringDialogProps> = ({
  isOpen,
  onClose,
  tournament,
}) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRound, setSelectedRound] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [scoringMatch, setScoringMatch] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchMatches();
      setupRealtimeSubscription();
    }

    return () => {
      // Clean up subscription when dialog closes
      supabase.removeAllChannels();
    };
  }, [isOpen, tournament.id]);

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('match-scoring-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournament_matches',
          filter: `tournament_id=eq.${tournament.id}`
        },
        () => {
          console.log("🔄 Real-time match update detected, refreshing...");
          fetchMatches();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchMatches = async () => {
    console.log("🏆 Fetching tournament matches for scoring...");
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from("tournament_matches")
        .select(`
          *,
          venues(name)
        `)
        .eq("tournament_id", tournament.id)
        .order("round_number")
        .order("match_number");

      if (error) throw error;

      console.log("📊 Matches fetched for scoring:", data);
      setMatches(data || []);
    } catch (error: any) {
      console.error("❌ Error fetching matches:", error);
      toast.error("Failed to load matches");
    } finally {
      setIsLoading(false);
    }
  };

  const updateMatchScore = async (matchId: string, team1Score: number, team2Score: number) => {
    setScoringMatch(matchId);
    
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match) throw new Error("Match not found");

      // Determine winner
      let winnerId = null;
      let loserId = null;
      let status: 'scheduled' | 'in_progress' | 'completed' = 'in_progress';
      
      if (team1Score !== team2Score && (team1Score > 0 || team2Score > 0)) {
        winnerId = team1Score > team2Score ? match.team1_id : match.team2_id;
        loserId = team1Score > team2Score ? match.team2_id : match.team1_id;
        status = 'completed';
      }

      const { error } = await supabase
        .from("tournament_matches")
        .update({
          team1_score: team1Score,
          team2_score: team2Score,
          winner_id: winnerId,
          status: status,
        })
        .eq("id", matchId);

      if (error) throw error;

      // Update local state immediately for better UX
      setMatches(prev => prev.map(m => 
        m.id === matchId 
          ? { ...m, team1_score: team1Score, team2_score: team2Score, winner_id: winnerId, status }
          : m
      ));

      toast.success(`Match score updated: ${team1Score} - ${team2Score}`);
      
      if (status === 'completed' && winnerId) {
        const winnerName = winnerId === match.team1_id ? match.team1_name : match.team2_name;
        toast.success(`🏆 Match completed! Winner: ${winnerName}`);
        
        // Fetch current match with next_match info for bracket progression
        const { data: currentMatch } = await supabase
          .from('tournament_matches')
          .select('next_match_id, next_match_slot, loser_next_match_id, loser_next_match_slot')
          .eq('id', matchId)
          .single();

        if (currentMatch) {
          await handleBracketProgression(
            { ...match, ...currentMatch },
            winnerId,
            loserId
          );
        }
      }
    } catch (error: any) {
      console.error("❌ Error updating match score:", error);
      toast.error("Failed to update match score");
    } finally {
      setScoringMatch(null);
    }
  };

  const handleBracketProgression = async (
    completedMatch: Match & { next_match_id?: string; next_match_slot?: number; loser_next_match_id?: string; loser_next_match_slot?: number },
    winnerId: string,
    loserId?: string | null
  ) => {
    console.log('[BRACKET] Processing advancement for match:', completedMatch.id);
    console.log('[BRACKET] Winner:', winnerId, 'Loser:', loserId);

    try {
      // 1. Advance winner to next match
      if (completedMatch.next_match_id && completedMatch.next_match_slot) {
        const updateField = completedMatch.next_match_slot === 1 ? 'team1_id' : 'team2_id';
        const updateNameField = completedMatch.next_match_slot === 1 ? 'team1_name' : 'team2_name';
        
        // Get winner name
        const winnerName = winnerId === completedMatch.team1_id ? completedMatch.team1_name : completedMatch.team2_name;
        
        const { error: winnerError } = await supabase
          .from('tournament_matches')
          .update({ 
            [updateField]: winnerId,
            [updateNameField]: winnerName
          })
          .eq('id', completedMatch.next_match_id);

        if (winnerError) {
          console.error('[BRACKET] Error advancing winner:', winnerError);
          toast.error('Failed to advance winner to next match');
        } else {
          console.log('[BRACKET] Winner advanced to match', completedMatch.next_match_id, 'slot', completedMatch.next_match_slot);
          toast.success(`🏆 ${winnerName} advanced to next round!`);
        }
      }

      // 2. For double elimination, advance loser to losers bracket
      if (completedMatch.loser_next_match_id && completedMatch.loser_next_match_slot && loserId) {
        const updateField = completedMatch.loser_next_match_slot === 1 ? 'team1_id' : 'team2_id';
        const updateNameField = completedMatch.loser_next_match_slot === 1 ? 'team1_name' : 'team2_name';
        
        // Get loser name
        const loserName = loserId === completedMatch.team1_id ? completedMatch.team1_name : completedMatch.team2_name;
        
        const { error: loserError } = await supabase
          .from('tournament_matches')
          .update({ 
            [updateField]: loserId,
            [updateNameField]: loserName
          })
          .eq('id', completedMatch.loser_next_match_id);

        if (loserError) {
          console.error('[BRACKET] Error moving loser to losers bracket:', loserError);
        } else {
          console.log('[BRACKET] Loser moved to match', completedMatch.loser_next_match_id);
        }
      }

      // 3. Check if next match is now ready to play (both teams assigned)
      if (completedMatch.next_match_id) {
        const { data: nextMatch } = await supabase
          .from('tournament_matches')
          .select('team1_id, team2_id, status')
          .eq('id', completedMatch.next_match_id)
          .single();

        if (nextMatch?.team1_id && nextMatch?.team2_id && nextMatch.status === 'scheduled') {
          console.log('[BRACKET] Next match is ready to play');
        }
      }

      // Refresh matches to show updated bracket
      fetchMatches();

    } catch (error) {
      console.error('[BRACKET] Progression error:', error);
    }
  };

  const updateMatchStatus = async (matchId: string, newStatus: 'scheduled' | 'in_progress' | 'completed') => {
    try {
      const { error } = await supabase
        .from("tournament_matches")
        .update({ status: newStatus })
        .eq("id", matchId);

      if (error) throw error;

      setMatches(prev => prev.map(m => 
        m.id === matchId ? { ...m, status: newStatus } : m
      ));

      toast.success(`Match status updated to ${newStatus}`);
    } catch (error: any) {
      console.error("❌ Error updating match status:", error);
      toast.error("Failed to update match status");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'in_progress':
        return <Play className="h-4 w-4 text-orange-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case 'in_progress':
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case 'completed':
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getBracketTypeDisplay = (type: string) => {
    switch (type) {
      case "winners":
        return "Winners Bracket";
      case "losers":
        return "Losers Bracket";
      case "pool":
        return "Pool Play";
      case "final":
        return "Finals";
      default:
        return type;
    }
  };

  const filteredMatches = matches.filter(match => {
    const roundMatch = selectedRound === "all" || match.round_number.toString() === selectedRound;
    const statusMatch = selectedStatus === "all" || match.status === selectedStatus;
    return roundMatch && statusMatch;
  });

  const rounds = [...new Set(matches.map(m => m.round_number))].sort((a, b) => a - b);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Match Scoring - {tournament.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Tournament Info */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-muted-foreground">Tournament:</span>
                  <div>{tournament.name}</div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Status:</span>
                  <div className="capitalize">{tournament.status}</div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Total Matches:</span>
                  <div className="flex items-center gap-1">
                    <Trophy className="h-4 w-4" />
                    {matches.length}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="round-filter">Filter by Round</Label>
              <Select value={selectedRound} onValueChange={setSelectedRound}>
                <SelectTrigger>
                  <SelectValue placeholder="All Rounds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rounds</SelectItem>
                  {rounds.map(round => (
                    <SelectItem key={round} value={round.toString()}>
                      Round {round}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="status-filter">Filter by Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Matches List */}
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="flex-shrink-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Tournament Matches ({filteredMatches.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto min-h-0 space-y-4 max-h-96"
              style={{ scrollbarWidth: 'thin' }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredMatches.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No matches found</h3>
                  <p className="text-muted-foreground">
                    Generate bracket first or adjust filters
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMatches.map((match) => (
                    <MatchScoringCard
                      key={match.id}
                      match={match}
                      isScoring={scoringMatch === match.id}
                      onUpdateScore={updateMatchScore}
                      onUpdateStatus={updateMatchStatus}
                      getStatusIcon={getStatusIcon}
                      getStatusColor={getStatusColor}
                      getBracketTypeDisplay={getBracketTypeDisplay}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface MatchScoringCardProps {
  match: Match;
  isScoring: boolean;
  onUpdateScore: (matchId: string, team1Score: number, team2Score: number) => void;
  onUpdateStatus: (matchId: string, status: 'scheduled' | 'in_progress' | 'completed') => void;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusColor: (status: string) => string;
  getBracketTypeDisplay: (type: string) => string;
}

const MatchScoringCard: React.FC<MatchScoringCardProps> = ({
  match,
  isScoring,
  onUpdateScore,
  onUpdateStatus,
  getStatusIcon,
  getStatusColor,
  getBracketTypeDisplay,
}) => {
  const [team1Score, setTeam1Score] = useState(match.team1_score || 0);
  const [team2Score, setTeam2Score] = useState(match.team2_score || 0);

  useEffect(() => {
    setTeam1Score(match.team1_score || 0);
    setTeam2Score(match.team2_score || 0);
  }, [match.team1_score, match.team2_score]);

  const handleScoreSubmit = () => {
    onUpdateScore(match.id, team1Score, team2Score);
  };

  const isWinner = (teamId: string | undefined) => {
    return match.winner_id === teamId && match.status === 'completed';
  };

  return (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Match Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="font-medium">Round {match.round_number}</span>
                <span className="text-muted-foreground"> • Match {match.match_number}</span>
              </div>
              <Badge variant="outline">{getBracketTypeDisplay(match.bracket_type)}</Badge>
              <Badge variant="secondary" className={getStatusColor(match.status)}>
                <div className="flex items-center gap-1">
                  {getStatusIcon(match.status)}
                  {match.status}
                </div>
              </Badge>
            </div>
            
            {match.scheduled_time && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {`${new Date(match.scheduled_time).toLocaleDateString()}, ${formatTime(match.scheduled_time)}`}
              </div>
            )}
          </div>

          {/* Match Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Team 1 */}
            <div className={`text-center p-3 border rounded-lg ${isWinner(match.team1_id) ? 'bg-green-50 border-green-200' : ''}`}>
              <div className="font-medium text-lg">
                {match.team1_name || "TBD"}
                {isWinner(match.team1_id) && <Trophy className="inline h-4 w-4 ml-1 text-yellow-500" />}
              </div>
              <div className="text-2xl font-bold text-primary mt-2">
                {match.team1_score !== null ? match.team1_score : '-'}
              </div>
            </div>

            {/* VS / Score Input */}
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="text-lg font-bold text-muted-foreground">VS</div>
              
              {match.status !== 'completed' && (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <Label htmlFor={`team1-score-${match.id}`} className="text-xs">Score</Label>
                    <Input
                      id={`team1-score-${match.id}`}
                      type="number"
                      min="0"
                      value={team1Score}
                      onChange={(e) => setTeam1Score(parseInt(e.target.value) || 0)}
                      className="w-16 h-8 text-center"
                    />
                  </div>
                  <div className="text-muted-foreground">-</div>
                  <div className="flex flex-col items-center gap-1">
                    <Label htmlFor={`team2-score-${match.id}`} className="text-xs">Score</Label>
                    <Input
                      id={`team2-score-${match.id}`}
                      type="number"
                      min="0"
                      value={team2Score}
                      onChange={(e) => setTeam2Score(parseInt(e.target.value) || 0)}
                      className="w-16 h-8 text-center"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {match.status !== 'completed' && (
                  <Button
                    size="sm"
                    onClick={handleScoreSubmit}
                    disabled={isScoring}
                    className="flex items-center gap-1"
                  >
                    {isScoring && <Loader2 className="h-3 w-3 animate-spin" />}
                    Update Score
                  </Button>
                )}
                
                <Select
                  value={match.status}
                  onValueChange={(value: 'scheduled' | 'in_progress' | 'completed') => 
                    onUpdateStatus(match.id, value)
                  }
                >
                  <SelectTrigger className="w-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Team 2 */}
            <div className={`text-center p-3 border rounded-lg ${isWinner(match.team2_id) ? 'bg-green-50 border-green-200' : ''}`}>
              <div className="font-medium text-lg">
                {match.team2_name || "TBD"}
                {isWinner(match.team2_id) && <Trophy className="inline h-4 w-4 ml-1 text-yellow-500" />}
              </div>
              <div className="text-2xl font-bold text-primary mt-2">
                {match.team2_score !== null ? match.team2_score : '-'}
              </div>
            </div>
          </div>

          {/* Venue Info */}
          {(match.venues || match.court_field) && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2 border-t">
              {match.venues && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {match.venues.name}
                </div>
              )}
              {match.court_field && (
                <div className="flex items-center gap-1">
                  <Square className="h-4 w-4" />
                  {match.court_field}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};